import { useState, type ReactNode } from 'react';
import { SlidersHorizontal } from 'lucide-react';
import { useIsMobile } from '@/hooks/useIsMobile';

/**
 * Mobile-only list controls that bring the desktop table's header filters to the
 * card layout (ported from the Project Tracker tenant). Renders nothing above
 * Tailwind's `md` breakpoint (via useIsMobile) — desktop keeps its header filter
 * row, and jsdom (matchMedia absent) never mounts these, so existing table-based
 * tests are unaffected and the duplicate `aria-label`s they would otherwise
 * introduce never appear.
 *
 * Presentational: they drive the SAME filter state the desktop header already
 * uses, so filtering behaviour is identical on both.
 */

/**
 * A collapsible "Filters" disclosure holding the caller's filter controls
 * (typically the same inputs the desktop header uses, each wrapped in a
 * MobileFilterField). Collapsed by default so the inputs aren't in the DOM until
 * opened — keeps the card list uncluttered and avoids duplicate controls.
 */
export function MobileFilterPanel({
  activeCount, onClear, children, className = '',
}: {
  activeCount: number;
  onClear?: () => void;
  children: ReactNode;
  className?: string;
}) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  if (!isMobile) return null;
  return (
    <div className={className}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ' +
          (activeCount > 0 ? 'border-orange bg-orange/10 text-orange' : 'border-border text-darkGrey/70')}
      >
        <SlidersHorizontal size={13} aria-hidden /> Filters{activeCount > 0 ? ` (${activeCount})` : ''}
      </button>
      {open && (
        <div className="mt-2 space-y-2 rounded-lg border border-border bg-white p-3 shadow-sm">
          {children}
          {onClear && activeCount > 0 && (
            <button type="button" onClick={onClear} className="text-xs font-semibold text-darkGrey/60 hover:text-navy">
              Clear filters ×
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** A labelled row inside MobileFilterPanel — the column name above its control. */
export function MobileFilterField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[0.62rem] font-bold uppercase tracking-wider text-darkGrey/45">{label}</span>
      {children}
    </label>
  );
}
